// Phrases lisibles du constructeur de formulaires (aperçu statique, éditeur de condition, fiche dossier). Fonctions
// pures, sans React. decrireCondition est LA formulation de référence d'une condition (libellé de la cible entre
// guillemets, dates en jj/mm/aaaa, valeurs libres entre guillemets) ; decrireConditionCible en est la forme qui part
// directement de la question cible (le ConditionEditor ne reçoit que ses candidates, pas la définition).
// Ré-exporté par ../services/formulairesServices avec ./types.
import {
  Condition, Definition, Question, EXTENSIONS_AUTORISEES, LIBELLES_FORMATS, LIBELLES_OPERATEURS, LIBELLES_TYPES,
  OPERATEURS_SANS_VALEUR, REGEX, optionsCible, trouverQuestion,
} from './types';

// Libellé d'une question, ou son id si le libellé est encore vide ; `id` sert de repli quand la question est introuvable
export const libelleQuestion = (q: Question | undefined, id?: string): string => {
  if (q && q.libelle && q.libelle.trim()) return q.libelle.trim();
  if (q) return q.id;
  return id || '?';
};

// aaaa-mm-jj -> jj/mm/aaaa (toute autre chaîne est rendue telle quelle)
export const formaterDateIso = (v: string): string =>
  REGEX.dateIso.test(v) ? `${v.slice(8, 10)}/${v.slice(5, 7)}/${v.slice(0, 4)}` : v;

// Une valeur de condition lisible selon la cible : libellé de l'option (choix, choix_multiple, oui_non), date
// jj/mm/aaaa, nombre tel quel, texte entre guillemets ; la valeur brute si la cible est inconnue.
export const libelleValeur = (cible: Question | undefined, v: string): string => {
  if (!cible) return v;
  const options = optionsCible(cible);
  if (options) {
    const o = options.filter((x) => x.valeur === v)[0];
    return o && o.libelle ? o.libelle : v;
  }
  if (cible.type === 'date') return formaterDateIso(v);
  if (cible.type === 'nombre') return v;
  return `« ${v} »`;
};

// Toutes les valeurs d'une condition, jointes par des virgules ('' avec rempli / vide ou sans valeur)
export const libellesValeurs = (cible: Question | undefined, c: Condition): string => {
  if (OPERATEURS_SANS_VALEUR.indexOf(c.op) >= 0 || c.valeur === undefined) return '';
  const valeurs = Array.isArray(c.valeur) ? c.valeur : [c.valeur];
  return valeurs.map((v) => libelleValeur(cible, v)).join(', ');
};

// « But du voyage » est égal à Affaires — depuis la question cible (undefined si introuvable : id brut)
export const decrireConditionCible = (cible: Question | undefined, c: Condition): string => {
  const op = LIBELLES_OPERATEURS[c.op] || c.op;
  const valeurs = libellesValeurs(cible, c);
  return `« ${libelleQuestion(cible, c.question)} » ${op}${valeurs ? ` ${valeurs}` : ''}`;
};

// Même phrase depuis la définition complète (aperçu statique, dialog)
export const decrireCondition = (def: Definition, c: Condition): string =>
  decrireConditionCible(trouverQuestion(def, c.question), c);

// Type lisible complété des attributs propres au type : « Texte court · E-mail », « Nombre (de 1950 à 2030) »,
// « Texte long (500 caractères max) » (le plafond par défaut n'est pas répété)
export const decrireType = (q: Question): string => {
  const base = LIBELLES_TYPES[q.type] || q.type;
  switch (q.type) {
    case 'texte':
      if (q.format && LIBELLES_FORMATS[q.format]) return `${base} · ${LIBELLES_FORMATS[q.format]}`;
      return q.max !== undefined && q.max < 2000 ? `${base} (${q.max} caractères max)` : base;
    case 'texte_long':
      return q.max !== undefined && q.max < 5000 ? `${base} (${q.max} caractères max)` : base;
    case 'nombre': {
      const aMin = q.min !== undefined;
      const aMax = q.max !== undefined;
      if (aMin && aMax) return `${base} (de ${q.min} à ${q.max})`;
      if (aMin) return `${base} (min ${q.min})`;
      if (aMax) return `${base} (max ${q.max})`;
      return base;
    }
    default:
      return base;
  }
};

// « fichiers : pdf, jpg (max 2) » pour une question fichier ('' pour les autres types)
export const decrireFichiers = (q: Question): string => {
  if (q.type !== 'fichier') return '';
  const accept = q.accept && q.accept.length ? q.accept : EXTENSIONS_AUTORISEES;
  const max = q.max_fichiers || 1;
  return `fichiers : ${accept.join(', ')} (max ${max})`;
};
